/**
 * Barely Worn — Native Shopify Storefront Scripts
 * Handles: Header scroll, Cart Drawer Ajax API, Delivery attributes,
 * Favourites (localStorage), Collection live filtering, and Product interactions.
 */

(function () {
  'use strict';

  // --- Utility: Toast Notifications ---
  function showToast(message) {
    const container = document.getElementById('toast-container');
    if (!container) return;
    const toast = document.createElement('div');
    toast.className = 'toast-msg';
    toast.textContent = message;
    container.appendChild(toast);
    setTimeout(() => {
      toast.style.opacity = '0';
      toast.style.transform = 'translateY(-10px)';
      toast.style.transition = 'all 0.3s ease';
      setTimeout(() => toast.remove(), 300);
    }, 3200);
  }

  // --- Utility: Money Formatter ---
  function formatMoney(cents) {
    const amount = (cents / 100).toFixed(2);
    const currency = window.BarelyWorn?.currency || 'ZAR';
    try {
      return new Intl.NumberFormat('en-ZA', { style: 'currency', currency: currency }).format(amount);
    } catch (e) {
      return 'R ' + amount;
    }
  }

  // =========================================================================
  // 1. Header Scroll & Mobile Nav
  // =========================================================================
  const siteHeader = document.getElementById('site-header');
  function handleScroll() {
    if (!siteHeader) return;
    if (window.scrollY > 20) {
      siteHeader.classList.add('is-scrolled');
    } else {
      siteHeader.classList.remove('is-scrolled');
    }
  }
  window.addEventListener('scroll', handleScroll, { passive: true });
  handleScroll();

  const navDrawer = document.getElementById('nav-drawer');
  const navDrawerOverlay = document.getElementById('nav-drawer-overlay');
  const navDrawerTrigger = document.getElementById('nav-drawer-trigger');
  const navDrawerClose = document.getElementById('nav-drawer-close');

  function openNavDrawer() {
    if (!navDrawer || !navDrawerOverlay) return;
    navDrawer.classList.add('is-open');
    navDrawerOverlay.classList.add('is-open');
    document.body.style.overflow = 'hidden';
    navDrawerTrigger?.setAttribute('aria-expanded', 'true');
    navDrawerTrigger?.querySelector('.icon-menu-open')?.classList.add('hidden');
    navDrawerTrigger?.querySelector('.icon-menu-close')?.classList.remove('hidden');
  }

  function closeNavDrawer() {
    if (!navDrawer || !navDrawerOverlay) return;
    navDrawer.classList.remove('is-open');
    navDrawerOverlay.classList.remove('is-open');
    document.body.style.overflow = '';
    navDrawerTrigger?.setAttribute('aria-expanded', 'false');
    navDrawerTrigger?.querySelector('.icon-menu-open')?.classList.remove('hidden');
    navDrawerTrigger?.querySelector('.icon-menu-close')?.classList.add('hidden');
  }

  if (navDrawerTrigger) {
    navDrawerTrigger.addEventListener('click', (e) => {
      e.preventDefault();
      if (navDrawer.classList.contains('is-open')) {
        closeNavDrawer();
      } else {
        openNavDrawer();
      }
    });
  }

  if (navDrawerClose) {
    navDrawerClose.addEventListener('click', (e) => {
      e.preventDefault();
      closeNavDrawer();
    });
  }

  if (navDrawerOverlay) {
    navDrawerOverlay.addEventListener('click', closeNavDrawer);
  }

  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && navDrawer?.classList.contains('is-open')) {
      closeNavDrawer();
    }
  });

  // =========================================================================
  // 2. Ajax Cart Drawer Management
  // =========================================================================
  const cartDrawer = document.getElementById('cart-drawer');
  const cartDrawerOverlay = document.getElementById('cart-drawer-overlay');
  const cartDrawerTrigger = document.getElementById('cart-drawer-trigger');
  const cartDrawerClose = document.getElementById('cart-drawer-close');
  const cartDeliveryToggle = document.getElementById('cart-delivery-toggle');
  const cartPickupDetails = document.getElementById('cart-pickup-details');
  const cartCountBadge = document.getElementById('cart-count-badge');
  const cartItemsContainer = document.getElementById('cart-drawer-items');
  const cartFooter = document.getElementById('cart-drawer-footer');
  const cartHoldNotice = document.getElementById('cart-drawer-hold-notice');
  const cartCountDesc = document.getElementById('cart-drawer-count-desc');
  const cartSubtotalEl = document.getElementById('cart-drawer-subtotal');
  const cartTotalEl = document.getElementById('cart-drawer-total');
  const cartAttrDeliveryInput = document.getElementById('cart-attr-delivery-method');
  const cartNoteInput = document.getElementById('cart-note-input');

  let currentCart = null;

  function openCartDrawer() {
    if (!cartDrawer || !cartDrawerOverlay) return;
    cartDrawer.classList.add('is-open');
    cartDrawerOverlay.classList.add('is-open');
    document.body.style.overflow = 'hidden';
    refreshCart();
  }

  function closeCartDrawer() {
    if (!cartDrawer || !cartDrawerOverlay) return;
    cartDrawer.classList.remove('is-open');
    cartDrawerOverlay.classList.remove('is-open');
    document.body.style.overflow = '';
  }

  if (cartDrawerTrigger) cartDrawerTrigger.addEventListener('click', openCartDrawer);
  if (cartDrawerClose) cartDrawerClose.addEventListener('click', closeCartDrawer);
  if (cartDrawerOverlay) cartDrawerOverlay.addEventListener('click', closeCartDrawer);

  const clearBagBtn = document.getElementById('cart-drawer-clear-btn');
  if (clearBagBtn) {
    clearBagBtn.addEventListener('click', async () => {
      clearBagBtn.disabled = true;
      try {
        await fetch('/cart/clear.js', { method: 'POST' });
        await refreshCart();
        showToast('Bag cleared');
      } catch (e) {
        console.error(e);
      } finally {
        clearBagBtn.disabled = false;
      }
    });
  }

  async function fetchCart() {
    try {
      const res = await fetch('/cart.js');
      return await res.json();
    } catch (e) {
      console.error('Failed to fetch cart:', e);
      return null;
    }
  }

  function renderCart(cart) {
    currentCart = cart;
    const count = cart.item_count;

    // Update count badges
    if (cartCountBadge) {
      cartCountBadge.textContent = count;
      if (count > 0) cartCountBadge.classList.remove('hidden');
      else cartCountBadge.classList.add('hidden');
    }

    if (cartCountDesc) {
      cartCountDesc.textContent = count === 0 ? 'Nothing here yet.' : `${count} pre-loved piece${count > 1 ? 's' : ''}`;
    }

    const clearBagBtnEl = document.getElementById('cart-drawer-clear-btn');
    if (clearBagBtnEl) {
      if (count === 0) clearBagBtnEl.classList.add('hidden');
      else clearBagBtnEl.classList.remove('hidden');
    }

    if (count === 0) {
      if (cartHoldNotice) cartHoldNotice.classList.add('hidden');
      if (cartFooter) cartFooter.classList.add('hidden');
      if (cartItemsContainer) {
        cartItemsContainer.innerHTML = `
          <div class="flex h-full flex-col items-center justify-center gap-4 py-16 text-center">
            <svg class="h-12 w-12 text-muted-foreground" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M6 2 3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4Z"/><path d="M3 6h18"/><path d="M16 10a4 4 0 0 1-8 0"/></svg>
            <p class="text-muted-foreground">Your bag is empty.</p>
            <a href="/collections/all" class="inline-flex items-center justify-center rounded-full bg-primary px-6 py-3 text-sm font-semibold text-primary-foreground transition hover:scale-105 shadow-sm">
              Start browsing
            </a>
          </div>
        `;
      }
      return;
    }

    // Has items
    if (cartHoldNotice) cartHoldNotice.classList.remove('hidden');
    if (cartFooter) cartFooter.classList.remove('hidden');

    let itemsHtml = '<ul class="space-y-3.5">';
    cart.items.forEach((item) => {
      const optionsHtml = item.options_with_values
        ? item.options_with_values.filter(o => o.value !== 'Default Title').map(o => `<p class="text-xs text-muted-foreground">${escapeHtml(o.name)}: ${escapeHtml(o.value)}</p>`).join('')
        : '';
      const isMulti = item.quantity > 1;
      const unitPrice = formatMoney(item.price);
      const linePrice = formatMoney(item.final_line_price);

      itemsHtml += `
        <li class="cart-drawer-item flex flex-col gap-2.5 rounded-2xl border border-border/80 bg-background p-3.5 transition shadow-xs" data-key="${item.key}" data-variant-id="${item.variant_id}">
          <div class="flex gap-3">
            <div class="h-20 w-16 shrink-0 overflow-hidden rounded-xl bg-muted border border-border/60">
              ${item.image ? `<img src="${item.image}" alt="${escapeHtml(item.title)}" class="h-full w-full object-cover">` : ''}
            </div>
            <div class="min-w-0 flex-1">
              ${item.vendor ? `<p class="text-[10px] font-bold uppercase tracking-wider text-primary">${escapeHtml(item.vendor)}</p>` : ''}
              <h4 class="truncate font-display text-base font-semibold leading-snug">
                <a href="${item.url}" class="hover:text-primary transition">${escapeHtml(item.product_title)}</a>
              </h4>
              ${optionsHtml}
              <div class="mt-1 flex items-baseline gap-2">
                <span class="font-display font-bold text-base text-primary">${linePrice}</span>
                ${isMulti ? `<span class="text-xs text-muted-foreground font-medium">(${unitPrice} each)</span>` : ''}
              </div>
            </div>
            <button type="button" class="cart-item-remove-btn self-start rounded-full p-2 text-muted-foreground hover:text-red-600 hover:bg-secondary transition" aria-label="Remove item" data-key="${item.key}">
              <svg class="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M3 6h18"/><path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"/><path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"/><line x1="10" x2="10" y1="11" y2="17"/><line x1="14" x2="14" y1="11" y2="17"/></svg>
            </button>
          </div>

          <!-- Quantity Stepper & Adjustments -->
          <div class="flex items-center justify-between pt-2 border-t border-border/40 text-xs">
            <div class="flex items-center gap-2">
              <span class="text-muted-foreground font-medium">Quantity:</span>
              <div class="inline-flex items-center rounded-lg border border-border bg-secondary/50">
                <button type="button" class="cart-qty-btn cart-qty-minus px-2 py-1 text-foreground hover:bg-secondary rounded-l-lg transition active:scale-95" data-key="${item.key}" data-new-qty="${item.quantity - 1}" aria-label="Decrease quantity">
                  <svg class="h-3 w-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><line x1="5" y1="12" x2="19" y2="12"/></svg>
                </button>
                <span class="px-2.5 font-bold text-foreground text-xs select-none">${item.quantity}</span>
                <button type="button" class="cart-qty-btn cart-qty-plus px-2 py-1 text-foreground hover:bg-secondary rounded-r-lg transition active:scale-95" data-key="${item.key}" data-new-qty="${item.quantity + 1}" data-current-qty="${item.quantity}" aria-label="Increase quantity">
                  <svg class="h-3 w-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
                </button>
              </div>
            </div>
            ${isMulti ? `<span class="inline-flex items-center gap-1 rounded-md bg-amber-500/10 px-2 py-0.5 text-[11px] font-semibold text-amber-700">Accidentally added 2? Click minus</span>` : `<span class="text-[11px] text-muted-foreground">One-of-a-kind piece</span>`}
          </div>
        </li>
      `;
    });
    itemsHtml += '</ul>';

    if (cartItemsContainer) cartItemsContainer.innerHTML = itemsHtml;
    updateCartTotals();
    bindCartItemControls();
    syncPDPCartState();
  }

  let selectedFulfillment = 'courier';
  let fulfillmentFee = 0;
  let fulfillmentLabel = 'Nationwide Courier';

  function setFulfillmentMethod(type, skipApi) {
    selectedFulfillment = type === 'pickup' ? 'pickup' : 'courier';
    fulfillmentFee = 0;
    fulfillmentLabel = selectedFulfillment === 'pickup'
      ? 'Free Store Pickup (Stocks Centre Pretoria)'
      : 'Nationwide Courier';

    // Update Cart Drawer UI
    const drawerFeeEl = document.getElementById('cart-drawer-fulfillment-fee');
    const drawerTextEl = document.getElementById('cart-drawer-fulfillment-text');
    const drawerTotalEl = document.getElementById('cart-drawer-total');
    const drawerChoiceInput = document.getElementById('cart-drawer-fulfillment-choice');

    if (drawerFeeEl) drawerFeeEl.textContent = 'Calculated at checkout';
    if (drawerTextEl) drawerTextEl.textContent = selectedFulfillment === 'pickup' ? 'Free In-Store Pickup' : 'Nationwide Courier';
    if (drawerChoiceInput) drawerChoiceInput.value = fulfillmentLabel;

    if (currentCart && drawerTotalEl) {
      drawerTotalEl.textContent = formatMoney(currentCart.total_price);
    }

    // Update Main Cart Page UI
    const mainFeeEl = document.getElementById('main-cart-fulfillment-fee');
    const mainTextEl = document.getElementById('main-cart-fulfillment-text');
    const mainTotalEl = document.getElementById('main-cart-total');
    const mainSubtotalEl = document.getElementById('main-cart-subtotal');
    const mainChoiceInput = document.getElementById('main-cart-fulfillment-choice');

    if (mainFeeEl) mainFeeEl.textContent = 'Calculated at checkout';
    if (mainTextEl) mainTextEl.textContent = selectedFulfillment === 'pickup' ? 'Free Boutique Pickup' : 'Door-to-Door Courier';
    if (mainChoiceInput) mainChoiceInput.value = fulfillmentLabel;

    if (mainSubtotalEl && currentCart) {
      mainSubtotalEl.textContent = formatMoney(currentCart.total_price);
    }
    if (mainTotalEl && currentCart) {
      mainTotalEl.textContent = formatMoney(currentCart.total_price);
    }
  }

  function updateCartTotals() {
    if (!currentCart) return;
    if (cartSubtotalEl) {
      cartSubtotalEl.textContent = formatMoney(currentCart.total_price);
    }
    const mainSubtotalEl = document.getElementById('main-cart-subtotal');
    if (mainSubtotalEl) {
      mainSubtotalEl.textContent = formatMoney(currentCart.total_price);
    }
    const drawerTotalEl = document.getElementById('cart-drawer-total');
    if (drawerTotalEl) {
      drawerTotalEl.textContent = formatMoney(currentCart.total_price);
    }
    const mainTotalEl = document.getElementById('main-cart-total');
    if (mainTotalEl) {
      mainTotalEl.textContent = formatMoney(currentCart.total_price);
    }
  }

  // Handle fulfillment card clicks (event delegation)
  document.addEventListener('click', (e) => {
    const card = e.target.closest('.cart-fulfillment-card');
    if (!card) return;
    const type = card.getAttribute('data-type');
    if (type) {
      setFulfillmentMethod(type);
    }
  });

  function isVariantInCart(variantId) {
    if (!currentCart || !currentCart.items) return false;
    const vIdNum = Number(variantId);
    return currentCart.items.some((item) => Number(item.variant_id) === vIdNum || Number(item.id) === vIdNum);
  }

  function getCartItemQuantity(variantId) {
    if (!currentCart || !currentCart.items) return 0;
    const vIdNum = Number(variantId);
    const item = currentCart.items.find((item) => Number(item.variant_id) === vIdNum || Number(item.id) === vIdNum);
    return item ? item.quantity : 0;
  }

  function syncPDPCartState() {
    if (!addToCartBtn) return;
    const currentVariantId = addToCartBtn.getAttribute('data-variant-id') || variantIdInput?.value;
    if (!currentVariantId) return;

    const inCartQty = getCartItemQuantity(currentVariantId);
    const btnText = addToCartBtn.querySelector('.btn-text');

    if (inCartQty > 0) {
      if (btnText) {
        btnText.innerHTML = `✓ In Your Bag (${inCartQty} reserved)`;
      }
      addToCartBtn.classList.add('bg-secondary', 'text-foreground', 'border', 'border-border');
      addToCartBtn.classList.remove('bg-primary', 'text-primary-foreground');
    } else {
      if (btnText) {
        btnText.innerHTML = 'Add to Bag';
      }
      addToCartBtn.classList.remove('bg-secondary', 'text-foreground', 'border', 'border-border');
      addToCartBtn.classList.add('bg-primary', 'text-primary-foreground');
    }
  }

  function bindCartItemControls() {
    // Remove buttons (cart drawer)
    document.querySelectorAll('.cart-item-remove-btn').forEach((btn) => {
      btn.onclick = async (e) => {
        e.preventDefault();
        const key = btn.getAttribute('data-key');
        if (!key) return;
        btn.disabled = true;
        try {
          const res = await fetch('/cart/change.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ id: key, quantity: 0 })
          });
          const updated = await res.json();
          renderCart(updated);
          showToast('Piece removed from bag');
        } catch (err) {
          console.error('Failed to remove item:', err);
        }
      };
    });

    // Quantity stepper buttons (cart-qty-minus and cart-qty-plus)
    document.querySelectorAll('.cart-qty-btn').forEach((btn) => {
      btn.onclick = async (e) => {
        e.preventDefault();
        const key = btn.getAttribute('data-key');
        const newQty = parseInt(btn.getAttribute('data-new-qty'), 10);
        if (!key || isNaN(newQty)) return;

        btn.disabled = true;
        try {
          const res = await fetch('/cart/change.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ id: key, quantity: newQty })
          });
          const updated = await res.json();
          if (res.ok) {
            renderCart(updated);
            if (newQty === 0) {
              showToast('Piece removed from bag');
            } else if (newQty === 1) {
              showToast('Quantity adjusted to 1');
            } else {
              showToast(`Quantity updated to ${newQty}`);
            }
          } else {
            showToast(updated.description || 'Only 1 of this unique piece is available in stock');
            await refreshCart();
          }
        } catch (err) {
          console.error('Failed to adjust quantity:', err);
        } finally {
          btn.disabled = false;
        }
      };
    });
  }

  async function refreshCart() {
    const cart = await fetchCart();
    if (cart) renderCart(cart);
    return cart;
  }

  // Quick Add from Product Cards
  document.addEventListener('click', async (e) => {
    const quickAddBtn = e.target.closest('.product-quick-add-btn');
    if (!quickAddBtn) return;

    e.preventDefault();
    const variantId = quickAddBtn.getAttribute('data-variant-id');
    const productTitle = quickAddBtn.getAttribute('data-product-title') || 'Piece';
    if (!variantId) return;

    // Check if item is already in bag (one-of-a-kind piece)
    if (!currentCart) await refreshCart();
    const inCartQty = getCartItemQuantity(variantId);
    if (inCartQty >= 1) {
      showToast(`"${productTitle}" is already in your bag!`);
      openCartDrawer();
      return;
    }

    quickAddBtn.disabled = true;
    try {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: variantId, quantity: 1 })
      });
      if (res.ok) {
        showToast(`${productTitle} added to your bag`);
        await refreshCart();
        openCartDrawer();
      } else {
        const error = await res.json();
        showToast(error.description || 'Could not add to bag');
      }
    } catch (err) {
      console.error('Error adding to cart:', err);
    } finally {
      quickAddBtn.disabled = false;
    }
  });

  // =========================================================================
  // 3. Favourites System (localStorage)
  // =========================================================================
  const FAV_STORAGE_KEY = 'barelyworn-favs';

  function getStoredFavs() {
    try {
      return JSON.parse(localStorage.getItem(FAV_STORAGE_KEY) || '[]');
    } catch {
      return [];
    }
  }

  function saveStoredFavs(favs) {
    try {
      localStorage.setItem(FAV_STORAGE_KEY, JSON.stringify(favs));
    } catch (e) {
      console.error(e);
    }
  }

  function syncFavUI() {
    const favs = getStoredFavs();
    const favCountBadge = document.getElementById('fav-count-badge');
    if (favCountBadge) {
      favCountBadge.textContent = favs.length;
      if (favs.length > 0) favCountBadge.classList.remove('hidden');
      else favCountBadge.classList.add('hidden');
    }

    document.querySelectorAll('.product-fav-btn').forEach((btn) => {
      const handle = btn.getAttribute('data-fav-handle');
      if (handle && favs.includes(handle)) {
        btn.classList.add('is-fav');
      } else {
        btn.classList.remove('is-fav');
      }
    });

    // Populate favourites page if active
    const favsGrid = document.getElementById('favs-grid');
    const favsEmpty = document.getElementById('favs-empty-state');
    const favsSummary = document.getElementById('favs-summary-bar');
    if (favsGrid && favsEmpty) {
      if (favs.length === 0) {
        favsGrid.classList.add('hidden');
        if (favsSummary) favsSummary.classList.add('hidden');
        favsEmpty.classList.remove('hidden');
        favsGrid.innerHTML = '';
      } else {
        favsEmpty.classList.add('hidden');
        renderFavouritesPage(favs);
      }
    }
  }

  document.addEventListener('click', (e) => {
    const favBtn = e.target.closest('.product-fav-btn');
    if (!favBtn) return;
    e.preventDefault();
    const handle = favBtn.getAttribute('data-fav-handle');
    if (!handle) return;

    let favs = getStoredFavs();
    if (favs.includes(handle)) {
      favs = favs.filter(h => h !== handle);
      showToast('Removed from favourites');
    } else {
      favs.push(handle);
      showToast('Saved to favourites');
    }
    saveStoredFavs(favs);
    syncFavUI();
  });

  // "Unfavourite all" / Clear rail button
  document.addEventListener('click', (e) => {
    const clearAllBtn = e.target.closest('#favs-clear-all-btn');
    if (!clearAllBtn) return;
    e.preventDefault();
    saveStoredFavs([]);
    syncFavUI();
    showToast('All saved pieces cleared from your rail');
    const soldNotice = document.getElementById('favs-sold-notice');
    if (soldNotice) soldNotice.classList.add('hidden');
  });

  // "Add all available pieces to bag" button
  document.addEventListener('click', async (e) => {
    const addAllBtn = e.target.closest('#favs-add-all-btn');
    if (!addAllBtn) return;
    e.preventDefault();

    const favsGrid = document.getElementById('favs-grid');
    if (!favsGrid) return;
    const cards = favsGrid.querySelectorAll('.product-card');
    const variantIds = [];
    cards.forEach((card) => {
      const vId = card.getAttribute('data-variant-id');
      const isAvail = card.getAttribute('data-available') === 'true';
      if (vId && isAvail && !variantIds.includes(Number(vId))) {
        variantIds.push(Number(vId));
      }
    });

    if (variantIds.length === 0) {
      showToast('No pieces currently available to add');
      return;
    }

    addAllBtn.disabled = true;
    const origHtml = addAllBtn.innerHTML;
    addAllBtn.innerHTML = '<span>Adding pieces...</span>';

    try {
      const itemsToAdd = variantIds.map(id => ({ id: id, quantity: 1 }));
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: itemsToAdd })
      });
      if (res.ok) {
        showToast(`Added ${variantIds.length} favourite piece${variantIds.length > 1 ? 's' : ''} to bag!`);
        await refreshCart();
        openCartDrawer();
      } else {
        const err = await res.json();
        showToast(err.description || 'Could not add all items to bag');
      }
    } catch (err) {
      console.error('Error adding all favourites to cart:', err);
      showToast('Could not add items to bag');
    } finally {
      addAllBtn.disabled = false;
      addAllBtn.innerHTML = origHtml;
    }
  });

  // Dismiss sold notice button
  document.addEventListener('click', (e) => {
    const dismissBtn = e.target.closest('#dismiss-sold-notice-btn');
    if (dismissBtn) {
      e.preventDefault();
      const soldNotice = document.getElementById('favs-sold-notice');
      if (soldNotice) soldNotice.classList.add('hidden');
    }
  });

  async function renderFavouritesPage(handles) {
    const favsGrid = document.getElementById('favs-grid');
    const favsSummary = document.getElementById('favs-summary-bar');
    const favsEmpty = document.getElementById('favs-empty-state');
    const favsSoldNotice = document.getElementById('favs-sold-notice');
    const favsSoldNoticeText = document.getElementById('favs-sold-notice-text');
    if (!favsGrid) return;

    if (!handles || handles.length === 0) {
      favsGrid.classList.add('hidden');
      if (favsSummary) favsSummary.classList.add('hidden');
      if (favsEmpty) favsEmpty.classList.remove('hidden');
      favsGrid.innerHTML = '';
      return;
    }

    favsGrid.classList.remove('hidden');
    favsGrid.innerHTML = '<div class="col-span-full py-16 text-center text-muted-foreground"><div class="inline-block h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent mb-3"></div><p class="text-sm font-medium">Checking boutique rails &amp; loading your saved pieces...</p></div>';

    try {
      const parser = new DOMParser();
      let activeCards = [];
      let soldHandles = [];

      await Promise.all(
        handles.map(async (handle) => {
          try {
            const res = await fetch(`/products/${handle}?view=card`);
            if (res.ok) {
              const html = await res.text();
              const doc = parser.parseFromString(html, 'text/html');
              const cardEl = doc.querySelector('.product-card');
              if (cardEl) {
                const isAvailable = cardEl.getAttribute('data-available') === 'true';
                if (!isAvailable) {
                  // Sold out piece!
                  soldHandles.push(handle);
                } else {
                  const cents = parseInt(cardEl.getAttribute('data-price-cents') || (parseFloat(cardEl.getAttribute('data-price') || '0') * 100), 10);
                  activeCards.push({
                    handle: handle,
                    html: cardEl.outerHTML,
                    priceCents: cents,
                    variantId: cardEl.getAttribute('data-variant-id')
                  });
                }
              }
            } else if (res.status === 404) {
              // Product deleted or unavailable
              soldHandles.push(handle);
            }
          } catch (e) {
            console.error('Failed to load card for:', handle, e);
          }
        })
      );

      // 1. Auto-unfavourite sold out items as requested
      if (soldHandles.length > 0) {
        let currentFavs = getStoredFavs();
        currentFavs = currentFavs.filter(h => !soldHandles.includes(h));
        saveStoredFavs(currentFavs);

        const favCountBadge = document.getElementById('fav-count-badge');
        if (favCountBadge) {
          favCountBadge.textContent = currentFavs.length;
          if (currentFavs.length > 0) favCountBadge.classList.remove('hidden');
          else favCountBadge.classList.add('hidden');
        }

        if (favsSoldNotice && favsSoldNoticeText) {
          favsSoldNoticeText.textContent = `${soldHandles.length} pre-loved piece${soldHandles.length > 1 ? 's were' : ' was'} purchased by another customer and automatically unfavourited from your rail.`;
          favsSoldNotice.classList.remove('hidden');
        }
      }

      // If all items were sold out or list is now empty
      if (activeCards.length === 0) {
        favsGrid.classList.add('hidden');
        if (favsSummary) favsSummary.classList.add('hidden');
        if (favsEmpty) favsEmpty.classList.remove('hidden');
        favsGrid.innerHTML = '';
        return;
      }

      // 2. Render available cards
      favsGrid.innerHTML = activeCards.map(c => c.html).join('');
      favsGrid.classList.remove('hidden');
      if (favsEmpty) favsEmpty.classList.add('hidden');

      // Bind heart states
      const savedHandles = getStoredFavs();
      favsGrid.querySelectorAll('.product-fav-btn').forEach((btn) => {
        btn.classList.toggle('is-fav', savedHandles.includes(btn.dataset.favHandle));
      });

      // 3. Compute running totals & discounts to entice purchase
      if (favsSummary) {
        favsSummary.classList.remove('hidden');
        const rawTotalCents = activeCards.reduce((acc, c) => acc + (c.priceCents || 0), 0);

        // Parse discount percent from settings (default 10%)
        let discountPercent = 10;
        const discountLabel = window.BarelyWorn?.promoDiscountLabel || '10% off';
        const match = discountLabel.match(/(\d+)%/);
        if (match) {
          discountPercent = parseInt(match[1], 10);
        }
        const promoCode = window.BarelyWorn?.welcomeCode || 'WELCOME10';

        const discountCents = Math.round(rawTotalCents * (discountPercent / 100));
        const finalCents = Math.max(0, rawTotalCents - discountCents);

        const rawTotalEl = document.getElementById('favs-total-raw');
        const discountedTotalEl = document.getElementById('favs-total-discounted');
        const savingsTagEl = document.getElementById('favs-savings-tag');

        if (rawTotalEl) rawTotalEl.textContent = formatMoney(rawTotalCents);
        if (discountedTotalEl) discountedTotalEl.textContent = formatMoney(finalCents);
        if (savingsTagEl) {
          savingsTagEl.textContent = `Save ${formatMoney(discountCents)} with code ${promoCode} (${discountPercent}% off)`;
        }
      }
    } catch (err) {
      console.error('Error rendering favourites page:', err);
      favsGrid.innerHTML = '<div class="col-span-full py-10 text-center text-muted-foreground">Could not load favourites.</div>';
    }
  }

  // =========================================================================
  // 4. Product Details Page Interactions
  // =========================================================================
  // Gallery thumbnails switcher
  document.querySelectorAll('.product-thumb-btn').forEach((btn) => {
    btn.addEventListener('click', () => {
      document.querySelectorAll('.product-thumb-btn').forEach(b => {
        b.classList.remove('border-primary');
        b.classList.add('border-transparent', 'opacity-60');
      });
      btn.classList.add('border-primary');
      btn.classList.remove('border-transparent', 'opacity-60');

      const newUrl = btn.getAttribute('data-media-url');
      const newAlt = btn.getAttribute('data-media-alt');
      const mainImg = document.getElementById('product-main-img');
      if (mainImg && newUrl) {
        mainImg.style.opacity = '0';
        setTimeout(() => {
          mainImg.src = newUrl;
          if (newAlt) mainImg.alt = newAlt;
          mainImg.style.opacity = '1';
        }, 150);
      }
    });
  });

  // Variant selector pills & select
  const variantSelect = document.getElementById('product-variant-select');
  const variantIdInput = document.getElementById('product-variant-id-input');
  const addToCartBtn = document.getElementById('product-add-to-cart-btn');
  const buyNowBtn = document.getElementById('product-buy-now-btn');
  const optionLabelEl = document.getElementById('pdp-selected-option-label');
  const priceBoxEl = document.getElementById('product-price-box');
  const variantPills = document.querySelectorAll('.pdp__pill');

  function updateSelectedVariant(variantId, variantTitle, variantPrice, isAvailable) {
    if (!variantId) return;
    if (variantIdInput) variantIdInput.value = variantId;
    if (optionLabelEl && variantTitle) optionLabelEl.textContent = variantTitle;

    // Sync pills active state
    variantPills.forEach((p) => {
      if (p.getAttribute('data-variant-id') === String(variantId)) {
        p.classList.add('is-active');
      } else {
        p.classList.remove('is-active');
      }
    });

    // Sync hidden select
    if (variantSelect && variantSelect.value !== String(variantId)) {
      variantSelect.value = variantId;
    }

    // Sync price if provided
    if (priceBoxEl && variantPrice) {
      const regPrice = priceBoxEl.querySelector('.price-item--regular');
      if (regPrice) regPrice.textContent = variantPrice;
    }

    // Sync Add to Bag button
    if (addToCartBtn) {
      addToCartBtn.setAttribute('data-variant-id', variantId);
      const btnLabel = addToCartBtn.querySelector('.btn-text span:not(.btn-spinner)');
      if (isAvailable) {
        addToCartBtn.disabled = false;
        addToCartBtn.classList.remove('opacity-50', 'pointer-events-none');
        if (btnLabel) btnLabel.textContent = 'Add to bag';
      } else {
        addToCartBtn.disabled = true;
        addToCartBtn.classList.add('opacity-50', 'pointer-events-none');
        if (btnLabel) btnLabel.textContent = 'Sold out';
      }
      syncPDPCartState();
    }

    // Sync Buy Now button
    if (buyNowBtn) {
      buyNowBtn.setAttribute('data-variant-id', variantId);
      if (isAvailable) {
        buyNowBtn.disabled = false;
        buyNowBtn.style.display = 'block';
      } else {
        buyNowBtn.disabled = true;
        buyNowBtn.style.display = 'none';
      }
    }
  }

  // Pill click listener
  variantPills.forEach((pill) => {
    pill.addEventListener('click', () => {
      const variantId = pill.getAttribute('data-variant-id');
      const variantTitle = pill.getAttribute('data-variant-title') || pill.textContent.trim();
      const variantPrice = pill.getAttribute('data-price');
      const isAvailable = pill.getAttribute('data-available') !== 'false' && !pill.disabled;
      updateSelectedVariant(variantId, variantTitle, variantPrice, isAvailable);
    });
  });

  if (variantSelect) {
    variantSelect.addEventListener('change', () => {
      const selectedOption = variantSelect.options[variantSelect.selectedIndex];
      if (!selectedOption) return;
      const variantId = selectedOption.value;
      const variantPrice = selectedOption.getAttribute('data-price');
      const isAvailable = selectedOption.getAttribute('data-available') === 'true';
      updateSelectedVariant(variantId, selectedOption.text.split('-')[0].trim(), variantPrice, isAvailable);
    });
  }

  if (addToCartBtn) {
    addToCartBtn.addEventListener('click', async () => {
      const variantId = addToCartBtn.getAttribute('data-variant-id') || variantIdInput?.value;
      if (!variantId) {
        showToast('Please select a piece option');
        return;
      }

      // Check if this piece is already in the bag
      if (!currentCart) await refreshCart();
      const inCartQty = getCartItemQuantity(variantId);
      if (inCartQty >= 1) {
        showToast('This unique piece is already in your bag! (Only 1 in stock)');
        openCartDrawer();
        return;
      }

      const btnText = addToCartBtn.querySelector('.btn-text');
      const btnSpinner = addToCartBtn.querySelector('.btn-spinner');
      if (btnText && btnSpinner) {
        btnText.classList.add('hidden');
        btnSpinner.classList.remove('hidden');
      }
      addToCartBtn.disabled = true;

      try {
        // Ensure courier fulfillment attribute is attached to order
        fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            attributes: {
              'Delivery_Method': 'Nationwide Courier Delivery (R150)',
              'Fulfillment_Choice': 'Nationwide Courier Delivery (R150)'
            }
          })
        }).catch(() => {});

        const res = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: variantId, quantity: 1 })
        });

        if (res.ok) {
          showToast('Added to your bag');
          await refreshCart();
          openCartDrawer();
        } else {
          const err = await res.json();
          showToast(err.description || 'Could not add piece to bag');
        }
      } catch (e) {
        console.error(e);
        showToast('Error adding to bag. Please try again.');
      } finally {
        if (btnText && btnSpinner) {
          btnText.classList.remove('hidden');
          btnSpinner.classList.add('hidden');
        }
        addToCartBtn.disabled = false;
        syncPDPCartState();
      }
    });
  }

  if (buyNowBtn) {
    buyNowBtn.addEventListener('click', async () => {
      const variantId = buyNowBtn.getAttribute('data-variant-id') || variantIdInput?.value;
      if (!variantId) {
        showToast('Please select a piece option');
        return;
      }

      // If already in cart, do not add duplicate! Proceed straight to checkout
      if (!currentCart) await refreshCart();
      const inCartQty = getCartItemQuantity(variantId);
      if (inCartQty >= 1) {
        window.location.href = '/checkout';
        return;
      }

      buyNowBtn.disabled = true;
      const origText = buyNowBtn.innerHTML;
      buyNowBtn.innerHTML = '<span>Directing to checkout...</span>';

      try {
        // Sync courier delivery attributes first
        await fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            attributes: {
              'Delivery_Method': 'Nationwide Courier Delivery (R150)',
              'Fulfillment_Choice': 'Nationwide Courier Delivery (R150)'
            }
          })
        });

        // Add variant to cart
        const addRes = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: variantId, quantity: 1 })
        });

        if (!addRes.ok) {
          const err = await addRes.json();
          // If already in cart or max inventory reached, proceed to checkout
          if (err.description && (err.description.includes('already') || err.description.includes('max') || err.description.includes('stock'))) {
            window.location.href = '/checkout';
            return;
          }
          showToast(err.description || 'Could not prepare checkout');
          buyNowBtn.disabled = false;
          buyNowBtn.innerHTML = origText;
          return;
        }

        // Successfully added, redirect to checkout
        window.location.href = '/checkout';
      } catch (e) {
        console.error('Buy Now error:', e);
        showToast('Could not load checkout. Please try again.');
        buyNowBtn.disabled = false;
        buyNowBtn.innerHTML = origText;
      }
    });
  }

  // =========================================================================
  // 5. Shop / Collection Live Filtering & Search
  // =========================================================================
  const shopGrid = document.getElementById('shop-products-grid');
  if (shopGrid) {
    initShopFiltering();
  }

  function initShopFiltering() {
    const searchInput = document.getElementById('shop-search-input');
    const sortSelect = document.getElementById('shop-sort-select');
    const countDisplay = document.getElementById('shop-results-count');
    const noResultsEl = document.getElementById('shop-no-results');
    const activeFilterBadge = document.getElementById('active-filter-badge');
    const headingPrefix = document.getElementById('heading-dept-prefix');

    // Parse URL query params and pathname
    const urlParams = new URLSearchParams(window.location.search);
    let activeDept = urlParams.get('dept') || '';
    if (!activeDept) {
      const path = window.location.pathname.toLowerCase();
      if (path.includes('women')) activeDept = 'Women';
      else if (path.includes('men')) activeDept = 'Men';
      else if (path.includes('accessor')) activeDept = 'Accessories';
    }
    let activeGender = urlParams.get('for') || '';
    let activeBudget = null;
    let activeNewOnly = false;
    let activeSizes = [];
    let activeBrands = [];
    let activeTypes = [];
    let activeMaxPrice = null;
    let activeInStockOnly = true;
    let searchQuery = '';

    // Collect all card nodes and their attributes
    const cards = Array.from(shopGrid.querySelectorAll('.product-card'));
    const allSizes = new Set();
    const allBrands = new Set();
    const allTypes = new Set();
    let highestPrice = 0;

    cards.forEach((card) => {
      const sz = card.getAttribute('data-size');
      const vd = card.getAttribute('data-vendor');
      const tp = card.getAttribute('data-type');
      const pr = parseFloat(card.getAttribute('data-price') || '0');

      if (sz && sz !== 'Default Title') allSizes.add(sz);
      if (vd) allBrands.add(vd);
      if (tp) allTypes.add(tp);
      if (pr > highestPrice) highestPrice = pr;
    });

    highestPrice = Math.ceil(highestPrice) || 1000;

    // Render Dynamic Chips in both desktop and mobile containers
    function renderDynamicChips() {
      const sizeContainers = document.querySelectorAll('.filter-chips-sizes');
      const typeContainers = document.querySelectorAll('.filter-chips-types');
      const brandContainers = document.querySelectorAll('.filter-chips-brands');
      const priceSliders = document.querySelectorAll('.filter-price-slider');
      const priceValLabels = document.querySelectorAll('.filter-price-val');

      // Populate Sizes
      const sortedSizes = Array.from(allSizes).sort();
      sizeContainers.forEach((ct) => {
        if (sortedSizes.length === 0) {
          ct.closest('.dynamic-facet-group')?.classList.add('hidden');
          return;
        }
        ct.innerHTML = sortedSizes
          .map(s => `<button type="button" class="filter-chip-size rounded-full border border-border px-4 py-1.5 text-sm hover:border-foreground transition" data-size="${s}">${s}</button>`)
          .join('');
      });

      // Populate Types
      const sortedTypes = Array.from(allTypes).sort();
      typeContainers.forEach((ct) => {
        if (sortedTypes.length === 0) {
          ct.closest('.dynamic-facet-group')?.classList.add('hidden');
          return;
        }
        ct.innerHTML = sortedTypes
          .map(t => `<button type="button" class="filter-chip-type rounded-full border border-border px-4 py-1.5 text-sm hover:border-foreground transition" data-type="${t}">${t}</button>`)
          .join('');
      });

      // Populate Brands (sanitizing chic-rewind / 3e0cg to Barelyworn Clothing)
      const sanitizedBrandSet = new Set();
      allBrands.forEach((b) => {
        const cleanName = /chic-rewind|3e0cg/i.test(b) ? 'Barelyworn Clothing' : b;
        if (cleanName) sanitizedBrandSet.add(cleanName);
      });
      const sortedBrands = Array.from(sanitizedBrandSet).sort();
      brandContainers.forEach((ct) => {
        if (sortedBrands.length === 0) {
          ct.closest('.dynamic-facet-group')?.classList.add('hidden');
          return;
        }
        ct.innerHTML = sortedBrands
          .map(b => `<button type="button" class="filter-chip-brand rounded-full border border-border px-4 py-1.5 text-sm hover:border-foreground transition" data-brand="${b}">${b}</button>`)
          .join('');
      });

      // Setup Price Sliders
      priceSliders.forEach((sl) => {
        sl.max = highestPrice;
        sl.value = highestPrice;
      });
      priceValLabels.forEach((lb) => {
        lb.textContent = `R${highestPrice}`;
      });
    }

    renderDynamicChips();

    // Main Filter & Sort Engine
    function applyFilters() {
      const term = searchQuery.trim().toLowerCase();
      let matchCount = 0;

      // Update Department Heading
      if (headingPrefix) {
        if (activeDept) {
          headingPrefix.textContent = (activeGender ? activeGender + "'s " : '') + activeDept;
        } else {
          headingPrefix.textContent = 'All';
        }
      }

      // Toggle "For" filter group visibility for Accessories
      const forGroups = document.querySelectorAll('[data-filter-type="gender"]');
      forGroups.forEach(g => {
        if (activeDept === 'Accessories') {
          g.classList.remove('hidden');
        } else {
          g.classList.add('hidden');
        }
      });

      cards.forEach((card) => {
        const dept = card.getAttribute('data-department');
        const gender = card.getAttribute('data-gender');
        const available = card.getAttribute('data-available') === 'true';
        const size = card.getAttribute('data-size');
        const vendor = card.getAttribute('data-vendor');
        const type = card.getAttribute('data-type');
        const price = parseFloat(card.getAttribute('data-price') || '0');
        const createdAtSec = parseInt(card.getAttribute('data-created-at') || '0', 10);
        const cardText = (card.textContent || '').toLowerCase();

        let visible = true;

        if (activeDept && (!dept || dept.toLowerCase() !== activeDept.toLowerCase())) visible = false;
        if (activeGender && gender !== activeGender) visible = false;
        if (activeInStockOnly && !available) visible = false;

        if (activeSizes.length > 0 && (!size || !activeSizes.includes(size))) visible = false;
        if (activeBrands.length > 0 && (!vendor || !activeBrands.includes(vendor))) visible = false;
        if (activeTypes.length > 0 && (!type || !activeTypes.includes(type))) visible = false;

        if (activeMaxPrice != null && price > activeMaxPrice) visible = false;

        if (activeBudget) {
          const [min, max] = activeBudget.split('-').map(Number);
          if (price < min || price >= max) visible = false;
        }

        if (activeNewOnly) {
          const ageDays = (Date.now() / 1000 - createdAtSec) / 86400;
          if (ageDays > 30) visible = false;
        }

        if (term && !cardText.includes(term)) visible = false;

        if (visible) {
          card.style.display = '';
          matchCount++;
        } else {
          card.style.display = 'none';
        }
      });

      // Sorting
      const sortVal = sortSelect?.value || 'new';
      const visibleCards = cards.filter(c => c.style.display !== 'none');
      visibleCards.sort((a, b) => {
        const priceA = parseFloat(a.getAttribute('data-price') || '0');
        const priceB = parseFloat(b.getAttribute('data-price') || '0');
        const timeA = parseInt(a.getAttribute('data-created-at') || '0', 10);
        const timeB = parseInt(b.getAttribute('data-created-at') || '0', 10);

        if (sortVal === 'low') return priceA - priceB;
        if (sortVal === 'high') return priceB - priceA;
        return timeB - timeA;
      });

      visibleCards.forEach(c => shopGrid.appendChild(c));

      // Update count & empty state
      if (countDisplay) countDisplay.textContent = `${matchCount} pieces`;
      if (noResultsEl) {
        if (matchCount === 0) noResultsEl.classList.remove('hidden');
        else noResultsEl.classList.add('hidden');
      }

      // Update badge
      const activeCount =
        (activeDept ? 1 : 0) +
        (activeGender ? 1 : 0) +
        (activeBudget ? 1 : 0) +
        (activeNewOnly ? 1 : 0) +
        activeSizes.length +
        activeBrands.length +
        activeTypes.length +
        (activeMaxPrice != null ? 1 : 0);

      if (activeFilterBadge) {
        activeFilterBadge.textContent = `(${activeCount})`;
        if (activeCount > 0) activeFilterBadge.classList.remove('hidden');
        else activeFilterBadge.classList.add('hidden');
      }

      const clearBtns = document.querySelectorAll('.filter-clear-btn');
      clearBtns.forEach(btn => {
        if (activeCount > 0) btn.classList.remove('hidden');
        else btn.classList.add('hidden');
      });
    }

    // Bind Filter Events
    document.addEventListener('click', (e) => {
      // Department chips
      const deptChip = e.target.closest('[data-dept]');
      if (deptChip) {
        activeDept = deptChip.getAttribute('data-dept');
        document.querySelectorAll('[data-dept]').forEach(c => {
          c.classList.remove('active', 'border-primary', 'bg-primary', 'text-primary-foreground');
          c.classList.add('border-border');
        });
        document.querySelectorAll(`[data-dept="${activeDept}"]`).forEach(c => {
          c.classList.add('active', 'border-primary', 'bg-primary', 'text-primary-foreground');
          c.classList.remove('border-border');
        });
        applyFilters();
        return;
      }

      // Gender chips
      const genderChip = e.target.closest('[data-gender]');
      if (genderChip) {
        activeGender = genderChip.getAttribute('data-gender');
        document.querySelectorAll('[data-gender]').forEach(c => {
          c.classList.remove('active', 'border-primary', 'bg-primary', 'text-primary-foreground');
          c.classList.add('border-border');
        });
        document.querySelectorAll(`[data-gender="${activeGender}"]`).forEach(c => {
          c.classList.add('active', 'border-primary', 'bg-primary', 'text-primary-foreground');
          c.classList.remove('border-border');
        });
        applyFilters();
        return;
      }

      // Newest chips
      const newChip = e.target.closest('[data-new]');
      if (newChip) {
        activeNewOnly = newChip.getAttribute('data-new') === 'true';
        document.querySelectorAll('[data-new]').forEach(c => {
          c.classList.remove('active', 'border-primary', 'bg-primary', 'text-primary-foreground');
          c.classList.add('border-border');
        });
        document.querySelectorAll(`[data-new="${activeNewOnly}"]`).forEach(c => {
          c.classList.add('active', 'border-primary', 'bg-primary', 'text-primary-foreground');
          c.classList.remove('border-border');
        });
        applyFilters();
        return;
      }

      // Budget chips
      const budgetChip = e.target.closest('[data-budget]');
      if (budgetChip) {
        const b = budgetChip.getAttribute('data-budget');
        activeBudget = (activeBudget === b) ? null : b;
        document.querySelectorAll('[data-budget]').forEach(c => {
          const val = c.getAttribute('data-budget');
          if (val === activeBudget) {
            c.classList.add('active', 'border-primary', 'bg-primary', 'text-primary-foreground');
            c.classList.remove('border-border');
          } else {
            c.classList.remove('active', 'border-primary', 'bg-primary', 'text-primary-foreground');
            c.classList.add('border-border');
          }
        });
        applyFilters();
        return;
      }

      // Size chip
      const sizeChip = e.target.closest('[data-size]');
      if (sizeChip) {
        const sz = sizeChip.getAttribute('data-size');
        if (activeSizes.includes(sz)) activeSizes = activeSizes.filter(x => x !== sz);
        else activeSizes.push(sz);
        document.querySelectorAll(`[data-size="${sz}"]`).forEach(c => {
          c.classList.toggle('active');
          c.classList.toggle('border-primary');
          c.classList.toggle('bg-primary');
          c.classList.toggle('text-primary-foreground');
        });
        applyFilters();
        return;
      }

      // Category chip
      const typeChip = e.target.closest('[data-type]');
      if (typeChip) {
        const tp = typeChip.getAttribute('data-type');
        if (activeTypes.includes(tp)) activeTypes = activeTypes.filter(x => x !== tp);
        else activeTypes.push(tp);
        document.querySelectorAll(`[data-type="${tp}"]`).forEach(c => {
          c.classList.toggle('active');
          c.classList.toggle('border-primary');
          c.classList.toggle('bg-primary');
          c.classList.toggle('text-primary-foreground');
        });
        applyFilters();
        return;
      }

      // Brand chip
      const brandChip = e.target.closest('[data-brand]');
      if (brandChip) {
        const bd = brandChip.getAttribute('data-brand');
        if (activeBrands.includes(bd)) activeBrands = activeBrands.filter(x => x !== bd);
        else activeBrands.push(bd);
        document.querySelectorAll(`[data-brand="${bd}"]`).forEach(c => {
          c.classList.toggle('active');
          c.classList.toggle('border-primary');
          c.classList.toggle('bg-primary');
          c.classList.toggle('text-primary-foreground');
        });
        applyFilters();
        return;
      }

      // Clear all
      const clearBtn = e.target.closest('.filter-clear-btn');
      if (clearBtn) {
        activeDept = '';
        activeGender = '';
        activeBudget = null;
        activeNewOnly = false;
        activeSizes = [];
        activeBrands = [];
        activeTypes = [];
        activeMaxPrice = null;
        searchQuery = '';
        if (searchInput) searchInput.value = '';

        document.querySelectorAll('.filter-chip, .filter-chip-gender, .filter-chip-new, .filter-chip-budget, .filter-chip-size, .filter-chip-type, .filter-chip-brand').forEach(c => {
          c.classList.remove('active', 'border-primary', 'bg-primary', 'text-primary-foreground');
          c.classList.add('border-border');
        });

        document.querySelectorAll('[data-dept=""], [data-gender=""], [data-new="false"]').forEach(c => {
          c.classList.add('active', 'border-primary', 'bg-primary', 'text-primary-foreground');
          c.classList.remove('border-border');
        });

        const priceSliders = document.querySelectorAll('.filter-price-slider');
        priceSliders.forEach(sl => sl.value = highestPrice);
        document.querySelectorAll('.filter-price-val').forEach(lb => lb.textContent = `R${highestPrice}`);

        applyFilters();
        return;
      }
    });

    // Price Slider Input
    document.querySelectorAll('.filter-price-slider').forEach(slider => {
      slider.addEventListener('input', (e) => {
        const val = parseFloat(e.target.value);
        activeMaxPrice = (val >= highestPrice) ? null : val;
        document.querySelectorAll('.filter-price-slider').forEach(s => s.value = val);
        document.querySelectorAll('.filter-price-val').forEach(lb => lb.textContent = `R${val}`);
        applyFilters();
      });
    });

    // In Stock Checkbox
    document.querySelectorAll('.filter-instock-checkbox').forEach(cb => {
      cb.addEventListener('change', (e) => {
        activeInStockOnly = e.target.checked;
        document.querySelectorAll('.filter-instock-checkbox').forEach(c => c.checked = activeInStockOnly);
        applyFilters();
      });
    });

    // Search Query Input
    if (searchInput) {
      searchInput.addEventListener('input', (e) => {
        searchQuery = e.target.value;
        applyFilters();
      });
    }

    // Sort Select
    if (sortSelect) {
      sortSelect.addEventListener('change', applyFilters);
    }

    // Drawer Open / Close (Desktop & Mobile)
    const filterOpenBtns = document.querySelectorAll('#shop-filter-open, #shop-mobile-filter-open');
    const mobileFilterClose = document.getElementById('shop-mobile-filter-close');
    const mobileFilterDrawer = document.getElementById('mobile-filter-drawer');
    const mobileFilterBackdrop = document.getElementById('mobile-filter-backdrop');

    if (mobileFilterDrawer && mobileFilterBackdrop) {
      filterOpenBtns.forEach(btn => {
        btn.addEventListener('click', () => {
          mobileFilterDrawer.classList.add('is-open');
          mobileFilterBackdrop.classList.add('is-open');
          document.body.style.overflow = 'hidden';
        });
      });
      mobileFilterClose?.addEventListener('click', () => {
        mobileFilterDrawer.classList.remove('is-open');
        mobileFilterBackdrop.classList.remove('is-open');
        document.body.style.overflow = '';
      });
      mobileFilterBackdrop.addEventListener('click', () => {
        mobileFilterDrawer.classList.remove('is-open');
        mobileFilterBackdrop.classList.remove('is-open');
        document.body.style.overflow = '';
      });
    }

    // Apply URL params initial state
    if (activeDept) {
      document.querySelectorAll(`[data-dept="${activeDept}"]`).forEach(c => {
        c.classList.add('active', 'border-primary', 'bg-primary', 'text-primary-foreground');
        c.classList.remove('border-border');
      });
      document.querySelectorAll('[data-dept=""]').forEach(c => {
        c.classList.remove('active', 'border-primary', 'bg-primary', 'text-primary-foreground');
        c.classList.add('border-border');
      });
    }

    applyFilters();

    // Ajax "Load more pieces" for collections with hundreds of items
    const loadMoreBtn = document.getElementById('shop-load-more-btn');
    if (loadMoreBtn) {
      loadMoreBtn.onclick = async () => {
        const nextUrl = loadMoreBtn.getAttribute('data-next-url');
        if (!nextUrl) return;

        const btnText = loadMoreBtn.querySelector('.btn-text');
        const btnSpinner = loadMoreBtn.querySelector('.btn-spinner');
        if (btnText && btnSpinner) {
          btnText.textContent = 'Loading pieces...';
          btnSpinner.classList.remove('hidden');
        }
        loadMoreBtn.disabled = true;

        try {
          const res = await fetch(nextUrl);
          const html = await res.text();
          const parser = new DOMParser();
          const doc = parser.parseFromString(html, 'text/html');

          const newCards = doc.querySelectorAll('#shop-products-grid .product-card');
          newCards.forEach((card) => {
            shopGrid.appendChild(card);
          });

          const newLoadMore = doc.getElementById('shop-load-more-btn');
          if (newLoadMore && newLoadMore.getAttribute('data-next-url')) {
            loadMoreBtn.setAttribute('data-next-url', newLoadMore.getAttribute('data-next-url'));
            if (btnText) btnText.textContent = 'Load more pieces';
            loadMoreBtn.disabled = false;
          } else {
            const paginationWrap = document.getElementById('shop-pagination-wrap');
            if (paginationWrap) paginationWrap.innerHTML = '<p class="text-xs text-muted-foreground font-semibold">You have viewed all pieces in this collection.</p>';
          }

          // Re-index favorites and filter system with new items
          syncFavUI();
          initShopFiltering();
        } catch (err) {
          console.error('Failed to load more products:', err);
          if (btnText) btnText.textContent = 'Load more pieces';
          loadMoreBtn.disabled = false;
        } finally {
          if (btnSpinner) btnSpinner.classList.add('hidden');
        }
      };
    }
  }

  // =========================================================================
  // 6. Interactive Customer Review Submission & Live Testimonial Feed
  // =========================================================================
  const reviewModal = document.getElementById('review-submission-modal');
  const reviewForm = document.getElementById('client-review-form');
  const starPicker = document.getElementById('review-star-picker');
  const ratingInput = document.getElementById('review-rating-input');
  const ratingLabel = document.getElementById('star-rating-label');
  const REVIEWS_STORAGE_KEY = 'barelyworn_user_reviews';

  function escapeHtml(str) {
    if (!str) return '';
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  function openReviewModal() {
    if (!reviewModal) return;
    reviewModal.classList.remove('opacity-0', 'pointer-events-none');
    reviewModal.classList.add('opacity-100', 'pointer-events-auto');
    const inner = reviewModal.querySelector('.relative');
    if (inner) {
      inner.classList.remove('scale-95');
      inner.classList.add('scale-100');
    }
    document.body.style.overflow = 'hidden';
  }

  function closeReviewModal() {
    if (!reviewModal) return;
    reviewModal.classList.add('opacity-0', 'pointer-events-none');
    reviewModal.classList.remove('opacity-100', 'pointer-events-auto');
    const inner = reviewModal.querySelector('.relative');
    if (inner) {
      inner.classList.add('scale-95');
      inner.classList.remove('scale-100');
    }
    document.body.style.overflow = '';
  }

  document.addEventListener('click', (e) => {
    if (e.target.closest('.open-review-modal-btn')) {
      e.preventDefault();
      openReviewModal();
    }
    if (e.target === reviewModal || e.target.closest('#close-review-modal-btn')) {
      closeReviewModal();
    }
    // Auth modal trigger from within review gate
    const authTrigger = e.target.closest('.open-auth-modal-btn');
    if (authTrigger) {
      closeReviewModal();
      const tab = authTrigger.getAttribute('data-auth-tab') || 'login';
      if (typeof openAuthModal === 'function') {
        openAuthModal(tab);
      }
    }
  });

  // SVG Half-Star & Full-Star Renderer
  function renderStarsHtml(rating, starClass = 'reviews-star') {
    const num = Math.round(parseFloat(rating || 5) * 2) / 2;
    let html = '';
    for (let s = 1; s <= 5; s++) {
      if (num >= s) {
        html += `<svg class="${starClass}" viewBox="0 0 24 24" style="color: #fbbc04; fill: #fbbc04;" aria-hidden="true"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>`;
      } else if (num >= s - 0.5) {
        html += `<svg class="${starClass}" viewBox="0 0 24 24" style="fill: url(#half-star-gradient);" aria-hidden="true"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>`;
      } else {
        html += `<svg class="${starClass} ${starClass}--off" viewBox="0 0 24 24" style="color: #d1d5db; fill: #d1d5db; opacity: 0.35;" aria-hidden="true"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>`;
      }
    }
    return html;
  }

  // Interactive Star Selection with Half-Star Support
  const starDesc = {
    '1.0': '1.0 ★ (Needs Improvement)',
    '1.5': '1.5 ★ (Below Average)',
    '2.0': '2.0 ★ (Fair)',
    '2.5': '2.5 ★ (Average)',
    '3.0': '3.0 ★ (Good)',
    '3.5': '3.5 ★ (Very Good)',
    '4.0': '4.0 ★ (Great)',
    '4.5': '4.5 ★★★★★ (Outstanding)',
    '5.0': '5.0 ★★★★★ (Exceptional)'
  };

  function setRatingVisual(val) {
    if (!starPicker) return;
    const num = Math.round(parseFloat(val || 5) * 2) / 2;
    const wrappers = starPicker.querySelectorAll('.star-wrapper');
    wrappers.forEach((wrap) => {
      const s = parseInt(wrap.getAttribute('data-star-index'), 10);
      const svg = wrap.querySelector('.star-icon');
      if (!svg) return;
      if (num >= s) {
        svg.style.fill = '#fbbc04';
        svg.style.color = '#fbbc04';
        svg.style.opacity = '1';
      } else if (num >= s - 0.5) {
        svg.style.fill = 'url(#half-star-gradient)';
        svg.style.opacity = '1';
      } else {
        svg.style.fill = '#d1d5db';
        svg.style.color = '#d1d5db';
        svg.style.opacity = '0.35';
      }
    });

    if (ratingLabel) {
      ratingLabel.textContent = starDesc[num.toFixed(1)] || `${num.toFixed(1)} ★`;
    }

    const pills = starPicker.querySelectorAll('.quick-rate-pill');
    pills.forEach((p) => {
      const pRate = parseFloat(p.getAttribute('data-rate'));
      if (Math.abs(pRate - num) < 0.05) {
        p.className = 'quick-rate-pill rounded-lg border border-primary bg-primary/10 px-2 py-1 text-[11px] font-semibold text-primary transition';
      } else {
        p.className = 'quick-rate-pill rounded-lg border border-border bg-card px-2 py-1 text-[11px] font-semibold text-foreground hover:border-primary transition';
      }
    });
  }

  function commitRating(val) {
    const num = Math.round(parseFloat(val) * 2) / 2;
    const clamped = Math.max(1.0, Math.min(5.0, num));
    if (starPicker) starPicker.setAttribute('data-rating', clamped.toFixed(1));
    if (ratingInput) ratingInput.value = clamped.toFixed(1);
    setRatingVisual(clamped);
  }

  if (starPicker) {
    starPicker.addEventListener('click', (e) => {
      const halfBtn = e.target.closest('.star-half-btn');
      if (halfBtn) {
        e.preventDefault();
        commitRating(halfBtn.getAttribute('data-val'));
        return;
      }
      const pill = e.target.closest('.quick-rate-pill');
      if (pill) {
        e.preventDefault();
        commitRating(pill.getAttribute('data-rate'));
        return;
      }
    });

    starPicker.addEventListener('mouseover', (e) => {
      const halfBtn = e.target.closest('.star-half-btn');
      if (halfBtn) {
        setRatingVisual(halfBtn.getAttribute('data-val'));
      }
    });

    starPicker.addEventListener('mouseleave', () => {
      const current = starPicker.getAttribute('data-rating') || '5.0';
      setRatingVisual(current);
    });
  }

  function isSampleReview(r) {
    if (!r || !r.text) return true;
    const txt = (r.text || '').toLowerCase();
    const nm = (r.name || '').toLowerCase();
    if (txt.includes('sample') || txt.includes('paste a real') || txt.includes('replace with a real')) return true;
    if (nm.includes('sample') || nm === 'customer') return true;
    return false;
  }

  function getStoredReviews() {
    let list = [];
    try {
      const raw = JSON.parse(localStorage.getItem(REVIEWS_STORAGE_KEY) || '[]');
      if (Array.isArray(raw)) {
        list = raw.filter((r) => !isSampleReview(r) && r.text.trim().length >= 5);
      }
    } catch {
      list = [];
    }

    // Merge genuine merchant reviews (if configured in theme customizer)
    const merchantScript = document.getElementById('merchant-reviews-data');
    if (merchantScript) {
      try {
        const merchantReviews = JSON.parse(merchantScript.textContent || '[]');
        if (Array.isArray(merchantReviews)) {
          merchantReviews.forEach((mRev) => {
            if (!isSampleReview(mRev)) {
              const exists = list.some((l) => l.text.trim() === mRev.text.trim());
              if (!exists) {
                list.push(mRev);
              }
            }
          });
        }
      } catch (e) {}
    }

    return list;
  }

  function saveStoredReview(rev) {
    const list = getStoredReviews();
    list.unshift(rev);
    localStorage.setItem(REVIEWS_STORAGE_KEY, JSON.stringify(list));
  }

  function updateReviewsSummaryBadge() {
    const scoreEl = document.getElementById('reviews-average-score');
    const countEl = document.getElementById('reviews-count-meta');
    const starsEl = document.getElementById('reviews-stars-visual');

    if (!scoreEl && !countEl && !starsEl) return;

    const reviews = getStoredReviews();
    const count = reviews.length;

    if (count === 0) {
      if (scoreEl) scoreEl.textContent = '5.0';
      if (countEl) countEl.textContent = '0 verified reviews';
      if (starsEl) starsEl.innerHTML = renderStarsHtml(5.0);
    } else {
      const totalStars = reviews.reduce((sum, r) => sum + (parseFloat(r.stars) || 5), 0);
      const avg = (totalStars / count).toFixed(1);
      if (scoreEl) scoreEl.textContent = avg;
      if (countEl) countEl.textContent = `${count} verified review${count > 1 ? 's' : ''}`;
      if (starsEl) starsEl.innerHTML = renderStarsHtml(avg);
    }
  }

  function buildReviewCardHtml(rev, isSpotlight = false) {
    const stars = parseFloat(rev.stars || '5');
    const starsHtml = renderStarsHtml(stars, 'reviews-star');
    const initial = (rev.name || 'C').charAt(0).toUpperCase();

    // Clean author name and location
    let locText = rev.location || '';
    let authorName = rev.name || 'Valued Customer';
    if (!locText && authorName.includes('(')) {
      const match = authorName.match(/\((.*?)\)/);
      if (match) {
        locText = match[1];
        authorName = authorName.replace(/\s*\(.*?\)/, '').trim();
      }
    } else if (authorName.includes('(')) {
      authorName = authorName.replace(/\s*\(.*?\)/, '').trim();
    }

    const locationHtml = locText
      ? `<span class="review-card__loc">
          <svg style="width: 12px; height: 12px; min-width: 12px; color: #c9a24d; display: inline-block; vertical-align: middle;" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/></svg>
          <span>${escapeHtml(locText)}</span>
         </span>`
      : '';

    return `
      <figure class="review-card ${isSpotlight ? 'review-card--spotlight' : ''}">
        <div class="review-card__top">
          <div class="review-stars-wrap">
            <span class="reviews-stars" aria-label="${stars} out of 5 stars">${starsHtml}</span>
            <span style="font-size: 0.9rem; font-weight: 700; color: #c9a24d; margin-left: 6px;">${stars.toFixed(1)}</span>
          </div>
          <span class="review-verified-pill">
            <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
              <path d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4zm-2 16l-4-4 1.41-1.41L10 14.17l6.59-6.59L18 9l-8 8z"/>
            </svg>
            Verified Buyer
          </span>
        </div>
        <blockquote class="review-card__text">${escapeHtml(rev.text)}</blockquote>
        <figcaption class="review-card__who">
          <span class="review-card__avatar">${initial}</span>
          <div class="review-card__author-info">
            <strong class="review-card__name">${escapeHtml(authorName)}</strong>
            <div style="display: flex; align-items: center; gap: 8px; flex-wrap: wrap;">
              ${locationHtml}
              <small class="review-card__date">${escapeHtml(rev.date || 'Verified Buyer')}</small>
            </div>
          </div>
        </figcaption>
      </figure>
    `;
  }

  function injectUserReviews() {
    const stored = getStoredReviews();
    const noReviewsBanner = document.getElementById('no-reviews-banner');
    const dynamicContainer = document.getElementById('dynamic-reviews-container');

    updateReviewsSummaryBadge();

    if (!stored.length) {
      if (noReviewsBanner) noReviewsBanner.classList.remove('hidden');
      if (dynamicContainer) {
        dynamicContainer.classList.add('hidden');
        dynamicContainer.innerHTML = '';
      }
      return;
    }

    if (noReviewsBanner) noReviewsBanner.classList.add('hidden');

    if (dynamicContainer) {
      dynamicContainer.classList.remove('hidden');

      // Build continuous seamless spinning marquee
      // Each half of marquee has at least 3 cards (minimum 6 cards total across the loop)
      const minPerHalf = Math.max(3, stored.length);
      const halfItems = [];
      while (halfItems.length < minPerHalf) {
        for (let i = 0; i < stored.length && halfItems.length < minPerHalf; i++) {
          halfItems.push(stored[i]);
        }
      }
      const marqueeList = halfItems.concat(halfItems);

      dynamicContainer.innerHTML = `
        <div class="reviews-viewport" style="--reviews-speed: 36s;">
          <div class="reviews-track" id="main-reviews-track">
            ${marqueeList.map(r => buildReviewCardHtml(r, false)).join('')}
          </div>
        </div>
      `;
    }
  }

  if (reviewForm) {
    reviewForm.addEventListener('submit', (e) => {
      e.preventDefault();
      const rating = parseFloat(document.getElementById('review-rating-input')?.value || '5.0');
      const nameInput = document.getElementById('review-author-name');
      const locInput = document.getElementById('review-author-location');
      const commentInput = document.getElementById('review-comment');

      const name = nameInput?.value.trim();
      const loc = locInput?.value.trim();
      const text = commentInput?.value.trim();

      // Mandatory field validation
      if (!name) {
        showToast('Please enter your display name to submit a review.', 'error');
        nameInput?.focus();
        return;
      }
      if (!loc) {
        showToast('Please enter your city/province (e.g. Pretoria East, GP).', 'error');
        locInput?.focus();
        return;
      }
      if (!text || text.length < 10) {
        showToast('Please write at least 10 characters describing your experience.', 'error');
        commentInput?.focus();
        return;
      }

      const reviewObj = {
        id: 'rev_' + Date.now(),
        name: name,
        location: loc,
        stars: rating,
        text: text,
        date: 'Just now'
      };

      saveStoredReview(reviewObj);
      injectUserReviews();

      closeReviewModal();
      reviewForm.reset();
      commitRating(5.0);

      showToast(`Thank you, ${name}! Your ${rating.toFixed(1)}★ verified review is now live in our testimonials.`);
    });
  }

  injectUserReviews();

  // =========================================================================
  // Post-Checkout Review Prompt
  // =========================================================================
  document.addEventListener('click', (e) => {
    const checkoutBtn = e.target.closest('#checkout-btn, #cart-drawer-checkout, [name="checkout"], a[href*="/checkout"]');
    if (checkoutBtn) {
      sessionStorage.setItem('barelyworn_checkout_active', 'true');
    }
  });

  (function initPostCheckoutReviewPrompt() {
    const hasCheckedOut = sessionStorage.getItem('barelyworn_checkout_active') === 'true';
    const isOrderRoute = window.location.pathname.includes('/orders/') || window.location.pathname.includes('/account') || window.location.search.includes('order_completed');
    const dismissedKey = 'barelyworn_review_prompt_dismissed';
    const isDismissed = localStorage.getItem(dismissedKey) === 'true';

    if ((hasCheckedOut || isOrderRoute) && !isDismissed) {
      setTimeout(() => {
        if (document.getElementById('post-checkout-prompt')) return;
        const promptEl = document.createElement('div');
        promptEl.id = 'post-checkout-prompt';
        promptEl.className = 'fixed bottom-5 right-5 z-50 max-w-sm rounded-3xl border border-[#c9a24d]/80 bg-espresso text-cream p-5 shadow-2xl backdrop-blur-md transition-all duration-500 transform translate-y-4 opacity-0 flex flex-col gap-3';
        promptEl.innerHTML = `
          <div class="flex items-start gap-3">
            <span class="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#fbbc04]/20 text-[#fbbc04]">
              <svg class="h-5 w-5 fill-current" viewBox="0 0 24 24"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>
            </span>
            <div class="flex-1">
              <h4 class="font-display text-base font-semibold text-white">Thank you for your order!</h4>
              <p class="text-xs text-cream/80 mt-1 leading-relaxed">How was your shopping experience? Once your pieces arrive or you collect in Pretoria, we'd love if you shared a quick review.</p>
            </div>
            <button type="button" id="close-post-checkout-prompt" class="text-cream/60 hover:text-white p-1 text-sm leading-none" aria-label="Dismiss">&times;</button>
          </div>
          <div class="flex items-center gap-2 pt-1">
            <button type="button" class="open-review-modal-btn flex-1 rounded-full bg-[#c9a24d] text-espresso py-2.5 px-4 text-xs font-bold uppercase tracking-wider shadow hover:scale-105 transition">
              Leave a review
            </button>
            <button type="button" id="dismiss-post-checkout-prompt" class="rounded-full border border-cream/30 py-2.5 px-4 text-xs font-semibold text-cream/80 hover:text-white transition">
              Maybe later
            </button>
          </div>
        `;
        document.body.appendChild(promptEl);
        requestAnimationFrame(() => {
          promptEl.classList.remove('translate-y-4', 'opacity-0');
        });

        const closeBtn = document.getElementById('close-post-checkout-prompt');
        const dismissBtn = document.getElementById('dismiss-post-checkout-prompt');
        const dismiss = () => {
          promptEl.classList.add('translate-y-4', 'opacity-0');
          setTimeout(() => promptEl.remove(), 400);
          localStorage.setItem(dismissedKey, 'true');
        };
        if (closeBtn) closeBtn.addEventListener('click', dismiss);
        if (dismissBtn) dismissBtn.addEventListener('click', dismiss);
      }, 2000);
    }
  })();

  // =========================================================================
  // 7. Account dropdown + Recently Viewed (browser-local, per device)
  // =========================================================================
  const acctMenu = document.getElementById('acct-menu');
  const acctTrigger = document.getElementById('acct-menu-trigger');
  if (acctMenu && acctTrigger) {
    acctTrigger.addEventListener('click', (e) => {
      e.stopPropagation();
      const open = acctMenu.classList.toggle('is-open');
      acctTrigger.setAttribute('aria-expanded', open ? 'true' : 'false');
    });
    document.addEventListener('click', (e) => {
      if (!acctMenu.contains(e.target)) {
        acctMenu.classList.remove('is-open');
        acctTrigger.setAttribute('aria-expanded', 'false');
      }
    });
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') acctMenu.classList.remove('is-open');
    });
  }

  const RECENT_KEY = 'barelyworn_recent';
  function readRecent() {
    try { return JSON.parse(localStorage.getItem(RECENT_KEY) || '[]'); } catch { return []; }
  }
  (function trackRecent() {
    const pdp = document.querySelector('.pdp');
    if (!pdp) return;
    const title = document.querySelector('.pdp__title')?.textContent.trim();
    const img = document.getElementById('product-main-img')?.getAttribute('src') || '';
    if (!title) return;
    const item = { title, url: window.location.pathname, img };
    const list = readRecent().filter((r) => r.url !== item.url);
    list.unshift(item);
    try { localStorage.setItem(RECENT_KEY, JSON.stringify(list.slice(0, 6))); } catch (e) {}
  })();
  const recentLink = document.getElementById('acct-recent-link');
  if (recentLink) {
    recentLink.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      let box = document.getElementById('acct-recent-box');
      if (box) { box.remove(); return; }
      const list = readRecent();
      box = document.createElement('div');
      box.id = 'acct-recent-box';
      box.className = 'acct-recent';
      if (!list.length) {
        box.innerHTML = '<p style="font-size:.78rem;padding:.4rem .6rem;color:#7a6a6a">Nothing viewed yet. Browse the shop and your pieces appear here.</p>';
      } else {
        box.innerHTML = list.map((r) => `<a href="${r.url}">${r.img ? `<img src="${r.img}" alt="" width="36" height="45">` : ''}<span>${r.title}</span></a>`).join('');
      }
      recentLink.insertAdjacentElement('afterend', box);
    });
  }

  // =========================================================================
  // 8. In-Theme Auth Modal (Sign In & Create Account)
  // =========================================================================
  const authModal = document.getElementById('auth-modal');
  const closeAuthModalBtn = document.getElementById('close-auth-modal-btn');
  const modalTabLogin = document.getElementById('modal-tab-login');
  const modalTabRegister = document.getElementById('modal-tab-register');
  const modalPaneLogin = document.getElementById('modal-pane-login');
  const modalPaneRegister = document.getElementById('modal-pane-register');

  function openAuthModal(mode = 'login') {
    if (!authModal) return;
    switchAuthModalTab(mode);
    authModal.classList.remove('opacity-0', 'pointer-events-none');
    const inner = authModal.querySelector('.transform');
    if (inner) inner.classList.remove('scale-95');
    document.body.style.overflow = 'hidden';
  }

  function closeAuthModal() {
    if (!authModal) return;
    authModal.classList.add('opacity-0', 'pointer-events-none');
    const inner = authModal.querySelector('.transform');
    if (inner) inner.classList.add('scale-95');
    document.body.style.overflow = '';
  }

  function switchAuthModalTab(mode) {
    if (mode === 'register') {
      if (modalTabRegister) {
        modalTabRegister.className = 'auth-btn-pill is-active flex-1 text-center py-2.5 px-4 rounded-full font-bold text-sm transition';
      }
      if (modalTabLogin) {
        modalTabLogin.className = 'auth-btn-pill flex-1 text-center py-2.5 px-4 rounded-full font-semibold text-sm transition';
      }
      if (modalPaneRegister) modalPaneRegister.classList.remove('hidden');
      if (modalPaneLogin) modalPaneLogin.classList.add('hidden');
    } else {
      if (modalTabLogin) {
        modalTabLogin.className = 'auth-btn-pill is-active flex-1 text-center py-2.5 px-4 rounded-full font-bold text-sm transition';
      }
      if (modalTabRegister) {
        modalTabRegister.className = 'auth-btn-pill flex-1 text-center py-2.5 px-4 rounded-full font-semibold text-sm transition';
      }
      if (modalPaneLogin) modalPaneLogin.classList.remove('hidden');
      if (modalPaneRegister) modalPaneRegister.classList.add('hidden');
    }
  }

  // Post-checkout / Order confirmation review prompt
  (function initPostCheckoutReviewPrompt() {
    const p = window.location.pathname.toLowerCase();
    const q = window.location.search.toLowerCase();
    const isPostCheckout = p.includes('/orders/') || p.includes('/order') || p.includes('thank_you') || q.includes('order') || q.includes('thank_you') || q.includes('completed');
    if (!isPostCheckout) return;

    const PROMPT_KEY = 'bw_checkout_review_prompted';
    try {
      if (sessionStorage.getItem(PROMPT_KEY)) return;
      sessionStorage.setItem(PROMPT_KEY, 'true');
    } catch (e) {}

    setTimeout(() => {
      const toastContainer = document.getElementById('toast-container');
      if (!toastContainer) return;
      const promptEl = document.createElement('div');
      promptEl.className = 'pointer-events-auto flex flex-col sm:flex-row items-center gap-4 rounded-2xl border-2 border-[#c9a24d] bg-background/95 p-4 shadow-2xl backdrop-blur-md max-w-lg transition duration-500';
      promptEl.innerHTML = `
        <div class="flex items-center gap-3">
          <span class="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#fbbc04]/20 text-[#fbbc04]">
            <svg class="h-5 w-5 fill-current" viewBox="0 0 24 24"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>
          </span>
          <div>
            <p class="font-display text-sm font-semibold text-foreground">Thank you for your boutique order!</p>
            <p class="text-xs text-muted-foreground">Share your feedback to help other pre-loved shoppers.</p>
          </div>
        </div>
        <div class="flex items-center gap-2 shrink-0">
          <button type="button" class="post-checkout-review-btn rounded-full bg-primary px-4 py-2 text-xs font-bold uppercase tracking-wider text-primary-foreground hover:scale-105 transition shadow-sm">
            Leave review
          </button>
          <button type="button" class="post-checkout-dismiss-btn rounded-full p-1.5 text-muted-foreground hover:bg-secondary transition" aria-label="Dismiss">
            <svg class="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M18 6 6 18"/><path d="m6 6 12 12"/></svg>
          </button>
        </div>
      `;
      toastContainer.appendChild(promptEl);

      promptEl.querySelector('.post-checkout-review-btn')?.addEventListener('click', () => {
        promptEl.remove();
        openReviewModal();
      });
      promptEl.querySelector('.post-checkout-dismiss-btn')?.addEventListener('click', () => {
        promptEl.remove();
      });
    }, 2500);
  })();

  if (modalTabLogin) modalTabLogin.addEventListener('click', () => switchAuthModalTab('login'));
  if (modalTabRegister) modalTabRegister.addEventListener('click', () => switchAuthModalTab('register'));
  if (closeAuthModalBtn) closeAuthModalBtn.addEventListener('click', closeAuthModal);
  document.addEventListener('click', (e) => {
    if (e.target.closest('.close-auth-modal-btn')) {
      e.preventDefault();
      closeAuthModal();
    }
  });
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && authModal && !authModal.classList.contains('opacity-0')) {
      closeAuthModal();
    }
  });
  if (authModal) {
    authModal.addEventListener('click', (e) => {
      if (e.target === authModal) closeAuthModal();
    });
  }

  // Intercept header & drawer Sign in / Create account triggers on storefront
  document.addEventListener('click', (e) => {
    const trigger = e.target.closest('a[href*="/account/login"], a[href*="/account/register"], .acct-menu__cta, .drawer-account-btn--signin, .drawer-account-btn--register, .open-auth-modal-btn');
    if (trigger && !window.location.pathname.includes('/account/login') && !window.location.pathname.includes('/account/register')) {
      const isRegister = trigger.classList.contains('acct-menu__cta--alt') || trigger.classList.contains('drawer-account-btn--register') || trigger.href?.includes('/register') || trigger.getAttribute('data-auth-tab') === 'register';
      e.preventDefault();
      closeNavDrawer();
      if (acctMenu) acctMenu.classList.remove('is-open');
      openAuthModal(isRegister ? 'register' : 'login');
    }
  });

  // Initial UI sync
  syncFavUI();
  bindCartItemControls();
  refreshCart();
})();
